import type {
  ImportConfirmRequest,
  ImportJob,
  ImportJobEvent,
  ImportStatus,
} from '@recipes/contracts';
import { Alert, Button, Spinner } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ImportReviewScreen } from '@/features/imports/import-review-screen';
import { useProfile } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { useSse } from '@/lib/sse/use-sse';
import { toast } from '@/components/feedback/toaster';

const TERMINAL: ImportStatus[] = ['REVIEW', 'CONFIRMED', 'FAILED', 'CANCELLED', 'EXPIRED'];
const PROGRESS_STEPS: ImportStatus[] = ['QUEUED', 'FETCHING', 'PARSING'];

export function ImportJobPage() {
  const { jobId = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  const jobQuery = useQuery({
    queryKey: queryKeys.imports.job(jobId),
    queryFn: () => api.imports.get(jobId),
    enabled: Boolean(jobId),
  });
  const job = jobQuery.data;
  const isTerminal = job ? TERMINAL.includes(job.status) : false;

  const onEvent = useCallback(
    (event: ImportJobEvent) => {
      if (event.type === 'status')
        queryClient.setQueryData(queryKeys.imports.job(jobId), event.job);
    },
    [jobId, queryClient],
  );
  const sse = useSse<ImportJobEvent>(jobId && !isTerminal ? api.imports.eventsUrl(jobId) : null, {
    onEvent,
    isTerminal: (e) =>
      e.type === 'done' || (e.type === 'status' && TERMINAL.includes(e.job.status)),
  });

  // Polling fallback every 2 s while SSE is unavailable and the job is still running.
  const polling = !isTerminal && (sse.status === 'error' || sse.status === 'closed');
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(
      () => void queryClient.invalidateQueries({ queryKey: queryKeys.imports.job(jobId) }),
      2000,
    );
    return () => clearInterval(id);
  }, [polling, jobId, queryClient]);

  const confirm = useMutation({
    mutationFn: (body: ImportConfirmRequest) => api.imports.confirm(jobId, body),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
      toast.success(t('imports.job.saved'));
      navigate(`/recipes/${res.recipeId}`);
    },
    onError: (e) => toastError(e, t('imports.job.confirmFailed')),
  });

  const retry = useMutation({
    mutationFn: () =>
      api.imports.fromUrl({ url: job?.url ?? '', allowAiFallback: false }, crypto.randomUUID()),
    onSuccess: (next) => navigate(`/add/import/${next.id}`, { replace: true }),
    onError: (e) => toastError(e),
  });

  useEffect(() => {
    if (job?.status === 'CONFIRMED' && job.recipeId)
      navigate(`/recipes/${job.recipeId}`, { replace: true });
  }, [job?.status, job?.recipeId, navigate]);

  if (jobQuery.isLoading) {
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  }
  if (jobQuery.error || !job) {
    return (
      <PageContainer>
        <Alert variant="destructive" title={t('imports.job.loadFailed')}>
          {describeError(jobQuery.error)}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={job.status === 'REVIEW' ? t('imports.job.reviewTitle') : t('imports.job.title')}
        description={job.url}
        backTo="/add/import"
      />
      {PROGRESS_STEPS.includes(job.status) && <ProgressSteps job={job} polling={polling} />}
      {job.status === 'FAILED' && (
        <Alert
          variant="destructive"
          title={t('imports.job.failedTitle')}
          data-testid="import-job-error"
        >
          <p>
            {t(`imports.errors.${job.errorCode ?? 'INTERNAL'}`, {
              defaultValue: job.errorMessage ?? '',
            })}
          </p>
          {job.errorMessage && (
            <p className="mt-1 text-xs text-muted-foreground">{job.errorMessage}</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{t('imports.errors.noBypass')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" loading={retry.isPending} onClick={() => retry.mutate()}>
              {t('app.retry')}
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to={`/add/import?url=${encodeURIComponent(job.url)}`}>
                {t('imports.job.editUrl')}
              </Link>
            </Button>
            <Button asChild size="sm" variant="ghost">
              <Link to="/recipes/new">{t('add.manual.title')}</Link>
            </Button>
          </div>
        </Alert>
      )}
      {(job.status === 'CANCELLED' || job.status === 'EXPIRED') && (
        <Alert variant="warning" title={t(`imports.job.status.${job.status}`)}>
          <Button asChild size="sm" variant="outline" className="mt-2">
            <Link to="/add/import">{t('imports.job.startOver')}</Link>
          </Button>
        </Alert>
      )}
      {job.status === 'REVIEW' && (
        <ImportReviewScreen
          job={job}
          profile={profile ?? null}
          confirming={confirm.isPending}
          onConfirm={(req) => confirm.mutateAsync(req).then(() => undefined)}
        />
      )}
    </PageContainer>
  );
}

function ProgressSteps({ job, polling }: { job: ImportJob; polling: boolean }) {
  const { t } = useTranslation();
  const currentIdx = PROGRESS_STEPS.indexOf(job.status);
  return (
    <div className="rounded-lg border p-4" role="status" aria-live="polite">
      <ol className="space-y-3">
        {PROGRESS_STEPS.map((step, idx) => {
          const state = idx < currentIdx ? 'done' : idx === currentIdx ? 'active' : 'todo';
          const Icon = state === 'done' ? CheckCircle2 : state === 'active' ? Loader2 : Circle;
          return (
            <li key={step} className="flex items-center gap-3 text-sm">
              <Icon
                className={
                  state === 'active'
                    ? 'size-5 animate-spin text-primary'
                    : 'size-5 text-muted-foreground'
                }
                aria-hidden
              />
              <span className={state === 'active' ? 'font-medium' : 'text-muted-foreground'}>
                {t(`imports.job.status.${step}`)}
              </span>
              <span className="sr-only">{t(`imports.job.stepState.${state}`)}</span>
            </li>
          );
        })}
      </ol>
      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className="h-full bg-primary transition-[width]"
          style={{ width: `${Math.max(5, job.progress)}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {job.progress}% {polling && `· ${t('imports.job.polling')}`}
      </p>
    </div>
  );
}
